const STORAGE_KEY = 'ws_traffic_source';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

interface TrafficSource {
  landing: string;
  referrer: string;
  utm: Partial<Record<(typeof UTM_KEYS)[number], string>>;
}

/**
 * Remembers where the visitor came from on their first page view of the session
 * (landing path, referrer, utm_* params). Used to attribute contact/early-access
 * requests to a source; nothing else is collected.
 */
export function captureTrafficSource(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    if (sessionStorage.getItem(STORAGE_KEY)) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const utm: TrafficSource['utm'] = {};
    for (const key of UTM_KEYS) {
      const value = params.get(key)?.trim();
      if (value) {
        utm[key] = value.slice(0, 100);
      }
    }
    const source: TrafficSource = {
      landing: window.location.pathname,
      referrer: document.referrer.slice(0, 300),
      utm,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(source));
  } catch {
    // sessionStorage may be unavailable (privacy mode); attribution is best-effort.
  }
}

/** One-line human-readable summary for the contact email, or '' if nothing is known. */
export function getTrafficSourceSummary(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return '';
    }
    const source = JSON.parse(raw) as TrafficSource;
    const parts: string[] = [];
    if (source.referrer) {
      parts.push(`referrer=${ source.referrer }`);
    }
    for (const [ key, value ] of Object.entries(source.utm)) {
      parts.push(`${ key }=${ value }`);
    }
    parts.push(`landing=${ source.landing }`);
    return parts.join(', ');
  } catch {
    return '';
  }
}
